import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Button } from "@material-ui/core";
import fieldStyles from "../account.module.css";
import modalStyles from "../changePassword/changePassword.module.css";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-material-ui";
import * as Yup from "yup";
import Alert from "../../../components/UI/alert";
import useAxiosPrivate from "../../../utils/hooks/useAxiosPrivate";
import { useDispatch } from "react-redux";
import { setChangeEmail } from "store/actions/authActions";
import ModalCloseButton from "../../../components/UI/ModalCloseButton";
import {
    centeredModalBoxSx,
    modalBackdropProps,
} from "../../../utils/styles/modal.overlay";

const style = centeredModalBoxSx({
    width: 650,
    maxWidth: "calc(100vw - 48px)",
    overflow: "visible",
    p: 0,
});

const inputClasses = {
    root: `${fieldStyles.loginFieldInputRoot} ${modalStyles.passwordInputRoot}`,
};

const hiddenLabelClasses = {
    root: `${fieldStyles.accountFieldLabel} ${fieldStyles.hiddenFieldLabel}`,
};

const ChangePasswordSchema = Yup.object().shape({
    newEmail: Yup.string()
        .email("Email must be a valid email")
        .required("New email doesn't exist!"),
    confirmEmail: Yup.string()
        .email("Email must be a valid email")
        .oneOf([Yup.ref("newEmail"), null], "Email addresses don't match.")
        .required("Confirm email doesn't exist!"),
});

export default function AccountModal(props) {
    const {
        setOpen,
        open,
        setOpenAuthentication,
        setSuccessEmail,
        verificationID,
        setOpenAuthenticationBefore,
    } = props;
    const axios = useAxiosPrivate();
    const dispatch = useDispatch();
    const initialValues = {
        newEmail: "",
        confirmEmail: "",
    };
    const [alert, setAlert] = useState({
        showAlert: false,
        severity: "success",
        message: "",
    });

    useEffect(() => {
        setOpenAuthentication(false);
        // eslint-disable-next-line
    }, [open]);

    const submit = async (e) => {
        try {
            const { data } = await axios.post("/user/change_email_request", {
                verification_id: verificationID,
                email: e.confirmEmail,
            });
            if (data?.message === "Pending admin approval!") {
                dispatch(setChangeEmail(e.confirmEmail));
                setSuccessEmail(true);
            } else {
                dispatch(setChangeEmail(e.confirmEmail));
                setOpenAuthenticationBefore(true);
            }
        } catch (error) {
            if (error.response && error.response?.data) {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: error.response?.data?.message,
                });
                setTimeout(() => {
                    setAlert({
                        showAlert: false,
                        severity: "error",
                        message: error.response?.data?.message,
                    });
                }, 5000);
            } else {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: "Something was wrong!",
                });
                setTimeout(() => {
                    setAlert({
                        showAlert: false,
                        severity: "error",
                        message: "Something was wrong!",
                    });
                }, 5000);
            }
        }
    };

    return (
        <div>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                BackdropProps={modalBackdropProps}
            >
                <Box sx={style}>
                    <div className={modalStyles.modalContent}>
                        <div className={modalStyles.closeRow}>
                            <ModalCloseButton
                                style={{ position: "static" }}
                                onClick={() => setOpen(false)}
                            />
                        </div>
                        <Formik
                            initialValues={initialValues}
                            onSubmit={submit}
                            validationSchema={ChangePasswordSchema}
                        >
                            {({ dirty, isValid }) => (
                                <Form className={modalStyles.form}>
                                    <div className={modalStyles.fieldBlock}>
                                        <label
                                            className={modalStyles.fieldLabel}
                                            htmlFor="change-email-new"
                                        >
                                            New Email Address
                                        </label>
                                        <Field
                                            id="change-email-new"
                                            name="newEmail"
                                            placeholder="Enter"
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            margin="none"
                                            type="email"
                                            className={modalStyles.passwordTextField}
                                            InputLabelProps={{ classes: hiddenLabelClasses }}
                                            InputProps={{
                                                classes: inputClasses,
                                                notched: false,
                                            }}
                                        />
                                    </div>

                                    <div className={modalStyles.fieldBlock}>
                                        <label
                                            className={modalStyles.fieldLabel}
                                            htmlFor="change-email-confirm"
                                        >
                                            Confirm Email Address
                                        </label>
                                        <Field
                                            id="change-email-confirm"
                                            name="confirmEmail"
                                            placeholder="Re-Enter"
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            margin="none"
                                            type="email"
                                            className={modalStyles.passwordTextField}
                                            InputLabelProps={{ classes: hiddenLabelClasses }}
                                            InputProps={{
                                                classes: inputClasses,
                                                notched: false,
                                            }}
                                        />
                                    </div>

                                    <div className={modalStyles.saveRow}>
                                        {dirty && isValid ? (
                                            <Button
                                                type="submit"
                                                disableRipple
                                                disableFocusRipple
                                                className={`${fieldStyles.nameActionButton} ${modalStyles.wideActionButton} ${fieldStyles.saveButton}`}
                                            >
                                                Send Request
                                            </Button>
                                        ) : (
                                            <Button
                                                type="button"
                                                disabled
                                                disableRipple
                                                disableFocusRipple
                                                className={`${fieldStyles.nameActionButton} ${modalStyles.wideActionButton} ${fieldStyles.saveButtonDisabled}`}
                                            >
                                                Send Request
                                            </Button>
                                        )}
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                    <Alert
                        showAlert={alert.showAlert}
                        severity={alert.severity}
                        message={alert.message}
                        setAlert={setAlert}
                    />
                </Box>
            </Modal>
        </div>
    );
}
